import classNames from 'classnames';
import * as React from 'react';
import { HtmlDiv } from '../Controls/HtmlDiv';
import { Picture } from '../Picture/Picture';
import type { BackgroundVariant } from '~/Blocks/ContentListBlock/BackgroundVariant.csharp';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import type { Theme } from '~/Utils/Theme';
import { themeDefaultValues } from '~/Utils/Theme';
import styles from './ContentListItem.module.css';
import type { ContentListItemProps } from './ContentListItemProps.csharp';

type ContentListItemType = 'horizontal' | 'vertical' | 'verticalNoImage';
type ContentListItemVariant = BackgroundVariant;

interface ContentListItemVariantProps {
    itemType: ContentListItemType;
    variant?: ContentListItemVariant;
    theme?: Pick<Theme, 'withRoundedCorners'>;
    className?: string;
    imageWrapperClassName?: string;
    titleClassName?: string;
    descriptionClassName?: string;
}

export const ContentListItem: React.FC<
    React.PropsWithChildren<ContentListItemProps & ContentListItemVariantProps>
> = props => {
    const {
        title,
        description,
        category,
        picture,
        link,
        itemType,
        variant,
        className,
        imageWrapperClassName,
        titleClassName,
        descriptionClassName,
        theme = themeDefaultValues,
    } = props;
    const { withRoundedCorners } = theme;

    const getTitleClassName = () =>
        classNames(styles.Title, link && link.href && styles.LinkTextUnderline, titleClassName);

    return (
        <div
            className={classNames(
                styles.ContentListItem,
                styles[`Variant--${itemType}`],
                variant && styles[`Variant--${variant}`],
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
        >
            <div className={classNames(styles.ImageWrapper, imageWrapperClassName)}>
                {picture ? (
                    <Picture {...picture} className={styles.Image} />
                ) : (
                    <PicturePlaceholder className={styles.Image} />
                )}
            </div>
            <div className={styles.Info}>
                {category && <span className={styles.Category}>{category}</span>}
                <div className={styles.TitleWrapper}>
                    <h3 className={getTitleClassName()}>{title}</h3>
                </div>
                {description && (
                    <HtmlDiv className={classNames(styles.Description, descriptionClassName)} html={description} />
                )}
            </div>
            {link && link.href && (
                <a href={link.href} className="ContentListItem__linkOverlay">
                    <span className="visuallyHidden">{link.label}</span>
                </a>
            )}
        </div>
    );
};
